import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import Button from '../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import LoaderButton from '../../generic/Button/LoaderButton';
import { RouterLink } from '../../generic/Link/index';
import usePortalUrl from '../../shared/publicPortal/hooks/usePortalUrl';

type Props = {
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  /** Whether a report is already published (toggles publish/replace wording). */
  isPublished: boolean;
  /** Label for the kind of report being published (e.g. "SBOM", "Licensing Report"). */
  reportLabel?: string;
};

/**
 * Confirmation dialog for publishing (or replacing) a report on the organization
 * portal. Surfaces the public portal URL the report will be available at, and a
 * link to the org portal settings.
 */
const PublishReportDialog: VoidFunctionComponent<Props> = ({
  onClose,
  onConfirm,
  isLoading,
  isPublished,
  reportLabel = 'SBOM',
}) => {
  const { url: portalUrl } = usePortalUrl();

  return (
    <Modal
      onHide={() => {
        // Keep the dialog open (no close) while the publish request is in flight.
        if (!isLoading) {
          onClose();
        }
      }}
      className="modal-confirm"
      show
    >
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>{isPublished ? `Replace ${reportLabel}` : `Publish ${reportLabel}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {portalUrl ? (
          <>
            <p>
              This {reportLabel} will be published to:{' '}
              <RouterLink to={portalUrl} openInNewTab>
                {portalUrl}
              </RouterLink>
            </p>
            <p>
              This is a public URL and will be visible to anyone you share it with. You can modify the portal in your{' '}
              <RouterLink to="/account/settings/organization/portal">Org Settings</RouterLink>.
            </p>
          </>
        ) : (
          <p>
            The report will not be accessible until you set up your organization portal. You can do this in{' '}
            <RouterLink to="/account/settings/organization/portal">Org Settings</RouterLink>.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM} onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <LoaderButton
            isLoading={isLoading}
            isDisabled={isLoading}
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            onClick={onConfirm}
          >
            {isPublished ? 'Republish' : 'Publish'}
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default PublishReportDialog;
